import type { ComponentChildren } from 'preact';
import {
  AlertTriangle,
  Banknote,
  BarChart,
  Building2,
  Coins,
  Landmark,
  LineChart,
  Percent,
  TrendingUp,
} from 'lucide-preact';
import {
  getOverviewBlockTitle,
  getVisibleKpiCards,
  isKpiCardVisible,
  type OverviewKpiCardConfig,
} from '../../content';
import { periodModeLabel, type PeriodMode } from '../../functions/calculations';
import type { OverviewKpiApiValues } from '../../features/overview/types';
import { SimpleCard } from './SimpleCard';

export interface KpiCardsRowProps extends OverviewKpiApiValues {
  loading: boolean;
  periodMode: PeriodMode;
}

const STATEMENT_PERIOD_CARD_IDS = new Set([
  'netEquity',
  'ttmCashReceived',
  'portfolioCoc',
  'capitalAtRisk',
]);

type KpiCardMeta = {
  icon: ComponentChildren;
  variant?: 'default' | 'warning' | 'risk';
  formatAsCurrency?: boolean;
};

const KPI_CARD_META: Record<string, KpiCardMeta> = {
  portfolioValue: { icon: <Building2 />, formatAsCurrency: true },
  netAnnualRentYield: { icon: <TrendingUp />, formatAsCurrency: true },
  ttmRoi: { icon: <BarChart /> },
  undistributedEarnings: { icon: <Coins />, formatAsCurrency: true },
  netEquity: { icon: <Landmark /> },
  ttmCashReceived: { icon: <Banknote /> },
  portfolioCoc: { icon: <Percent /> },
  portfolioNoiYield: { icon: <LineChart /> },
  capitalAtRisk: { icon: <AlertTriangle />, variant: 'risk' },
};

function getApiValue(id: string, apiValues: OverviewKpiApiValues): string {
  switch (id) {
    case 'portfolioValue':
      return `$${apiValues.portfolioValue}`;
    case 'netAnnualRentYield':
      return `$${apiValues.netAnnualRentYield}`;
    case 'ttmRoi':
      return apiValues.ttmRoi;
    case 'undistributedEarnings':
      return `$${apiValues.undistributedEarnings}`;
    case 'netEquity':
      return apiValues.netEquity;
    case 'ttmCashReceived':
      return apiValues.ttmCashReceived;
    case 'portfolioCoc':
      return apiValues.portfolioCoc;
    case 'portfolioNoiYield':
      return apiValues.portfolioNoiYield;
    case 'capitalAtRisk':
      return apiValues.capitalAtRisk;
    default:
      return '—';
  }
}

function getCardValue(card: OverviewKpiCardConfig, apiValues: OverviewKpiApiValues): string {
  if (card.useApi) {
    return getApiValue(card.id, apiValues);
  }

  return card.placeholder ?? '—';
}

function getCardFootnote(card: OverviewKpiCardConfig, apiValues: OverviewKpiApiValues): string | undefined {
  if (card.useApi && card.id === 'capitalAtRisk') {
    return apiValues.capitalAtRiskFootnote || undefined;
  }

  return card.footnote;
}

export const KPICardsRow = ({
  portfolioValue,
  netAnnualRentYield,
  ttmRoi,
  undistributedEarnings,
  netEquity,
  ttmCashReceived,
  portfolioCoc,
  portfolioNoiYield,
  capitalAtRisk,
  capitalAtRiskFootnote,
  loading,
  periodMode,
}: KpiCardsRowProps) => {
  const apiValues: OverviewKpiApiValues = {
    portfolioValue,
    netAnnualRentYield,
    ttmRoi,
    undistributedEarnings,
    netEquity,
    ttmCashReceived,
    portfolioCoc,
    portfolioNoiYield,
    capitalAtRisk,
    capitalAtRiskFootnote,
  };

  return (
    <>
      {getVisibleKpiCards().map((card) => {
        if (!isKpiCardVisible(card.view)) {
          return null;
        }

        const meta = KPI_CARD_META[card.id];
        if (!meta) {
          return null;
        }

        const title = card.id === 'ttmCashReceived'
          ? `${periodModeLabel(periodMode)} Cash Received`
          : getOverviewBlockTitle(card.id, card.id);
        const periodLabel = STATEMENT_PERIOD_CARD_IDS.has(card.id)
          ? periodModeLabel(periodMode)
          : 'TTM';

        return (
          <SimpleCard
            key={card.id}
            title={title.toUpperCase()}
            infoBlockId={card.id}
            value={getCardValue(card, apiValues)}
            periodLabel={periodLabel}
            headerIcon={meta.icon}
            footnote={getCardFootnote(card, apiValues)}
            variant={meta.variant ?? 'default'}
            loading={card.useApi ? loading : false}
          />
        );
      })}
    </>
  );
};
